/** Visits use Entity Board displays, a selection-driven Detail quick view, and a Manage inspector. */
import { ActivityEntityName, DomainType, EntityCalendarView, EntityViewDisplay, JobSecurityRight, PortalClientSecurityRight, SurfaceType, VisitBrowseRow } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { defaultCalendarEventMapper } from '@workbuddy/services/entities/entity.calendar.default.mapper';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityBoardPanelClick } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';
import { visitStateTag, visitStatusTag } from './presentation/visit.status.colour';
import { visitModifiedTag } from './presentation/visit.modified.tag';
import { visitDateTag } from './presentation/visit.date.tag';
import { visitApprovalTag } from './presentation/visit.approval.tag';
import { visitNeedsReviewTag } from './presentation/visit.needs.review.tag';
import { inject } from '@angular/core';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { FILTER_PERIOD_PRESETS, FilterPeriodPreset } from '@workbuddy/ui/v2/blocks/filter';
import { visitListDefaultPreset } from './board/visit.period.default';
import { pinVisitEventToLocalDay } from './board/visit.calendar.local.day';
import { VisitPlanGroupRowComponent } from './board/visit.plan.group.row/visit.plan.group.row.component';
import type { EntityManifestPresentation } from '@workbuddy/services/entities';
import { visitRowCommands } from './commands/visit.row.commands';
import { handleVisitBulkCommand, visitBulkCommands } from './commands/visit.bulk.commands';
import { visitAgeTag } from './presentation/visit.age.tag';
import { visitConstructionTag } from './presentation/visit.construction.tag';

// Repeated on the ClientPortal block: `resolveEntityManifest` builds a non-Tenant manifest from that
// entry alone, so a tag declared only on the base would leave portal calendar chips uncoloured.
const visitPresentation: EntityManifestPresentation<VisitBrowseRow> = {
  tags: {
    displayStatus: visitStateTag,
    approvalStatus: visitApprovalTag,
    state: visitStateTag,
    status: visitStatusTag,
    overridden: visitModifiedTag,
    bookedDate: visitDateTag,
    needsReview: visitNeedsReviewTag,
    ageState: visitAgeTag,
    constructionAttention: visitConstructionTag,
  },
};

const visitCalendar = {
  supportedViews: [EntityCalendarView.DayGridMonth, EntityCalendarView.DayGridWeek],
  defaultView: EntityCalendarView.DayGridMonth,
  interactions: { dateClick: false },
  // Both layouts are day grids, so a chip only names the day attendance falls on.
  eventMapper: (record: VisitBrowseRow, calendar) => {
    const event = defaultCalendarEventMapper(record, calendar);
    return event ? pinVisitEventToLocalDay({ ...event, allDay: record.allDay }, record.localDate) : null;
  },
};

const visitCalendarLayoutSettings = {
  contributions: [{
    id: 'visit-calendar-layout',
    when: ({ display }) => display === EntityViewDisplay.Calendar,
    controls: [{ kind: 'dropdown' as const, id: 'visitCalendarLayout', label: 'Calendar layout', path: 'calendar.view', default: EntityCalendarView.DayGridMonth,
      options: [{ id: EntityCalendarView.DayGridMonth, label: 'Month' }, { id: EntityCalendarView.DayGridWeek, label: 'Week' }] }],
  }],
};

export const visitManifest = registerManifest<VisitBrowseRow>({
  entity: ActivityEntityName.Visit,
  routes: {
    root: '/crm/visits',
    record: id => `/crm/visits/${encodeURIComponent(id)}`,
  },
  presentation: visitPresentation,
  board: {
    // Table only at tenant scope: about 5k Visits a day make a tenant-wide calendar unreadable and its
    // month read unbounded. Scoped relation mounts (plan, customer, site, line) opt back into Calendar.
    supportedDisplays: [EntityViewDisplay.Table],
    panel: { kind: 'quickview', width: 600, minWidth: 480, maxWidth: 900, ownsHeader: false },
    calendar: visitCalendar,
    defaults: {
      // `{ kind }` alone inherits the sizing declared on `board.panel` above (resolveBoardPanel's
      // partial-request merge) while adding the one thing that can only live on `defaults`: reaching
      // every mount — the entity board and the Customer, Site and Service Plan tabs alike — the same
      // way `period` below does. A Job's execution record supersedes the Planned attendance row that
      // spawned it, so a row carrying one opens the Job; a Job-less row still opens the Visit itself.
      panel: {
        kind: 'quickview',
        resolveTarget: ({ item }: EntityBoardPanelClick<VisitBrowseRow>) => (item.job?._id
          ? { entity: ActivityEntityName.Job, id: item.job._id }
          : { entity: ActivityEntityName.Visit, id: item._id }),
      },
      /**
       * Visits are read one period at a time, never unbounded.
       *
       * The occurrence reader walks a twelve-month projection, so a board that asks for "everything"
       * pays for all of it. On `defaults` rather than beside it, so the merge carries it to EVERY
       * mount — the entity board and the Customer, Site and Service Plan tabs alike — without those
       * mounts naming it. It bounds the read as a hidden predicate on `startDate`, the field the
       * Visit data strategy already resolves into its bounded `Period` browse scope.
       */
      period: {
        field: 'startDate',
        // All nine: a planner asks about work that has happened as readily as work that is coming.
        presets: [...FILTER_PERIOD_PRESETS],
        // Tenant policy, read per board: a planner arrives asking what is happening now.
        defaultPreset: () => visitListDefaultPreset(inject(GeneralSettingService).generalSettings),
        custom: true,
        // Table only. The Calendar bounds its own read by the grid it lays out, so a period
        // predicate on the same request would intersect that window and blank cells the grid has
        // already drawn. The anchor is still shared, so switching display keeps the same month.
        displays: [EntityViewDisplay.Table],
      },
      // Visits browse a forecast window (up to twelve months), and counting it exactly means walking
      // all of it — around 3s of a 4.5s page before WBS-11112. The reader reports `hasMore` instead.
      // The History scope still counts server-side, and a response carrying a real `total` keeps the
      // numbered paginator regardless of this flag, so declaring it here is safe for both scopes.
      paging: 'hasMore',
      table: {
        fillerColumn: true,
        // Overdue / Missed group by Service Plan on the server; the header shows what only the reader counts.
        serverGroupRow: VisitPlanGroupRowComponent,
        columns: {
          byField: {
            startDate: { headerName: 'Date / time', ...PlannedWorkCol.dateTime },
            servicePlan: PlannedWorkCol.title,
            customer: PlannedWorkCol.customer,
            site: PlannedWorkCol.site,
            serviceLine: PlannedWorkCol.line,
            resources: PlannedWorkCol.crew,
            contractor: PlannedWorkCol.crew,
            state: { headerName: 'Status', ...PlannedWorkCol.status },
            displayStatus: { headerName: 'Status', ...PlannedWorkCol.status },
            job: PlannedWorkCol.job,
          },
        },
      },
      // Tenant only: the portal block is read-only and never withdraws. A read-only mount (a paused
      // plan, a locked host) drops row commands, selection and bulk on its own.
      itemInteraction: {
        commands: visitRowCommands,
        selectable: {
          type: 'multiple',
          bulkCommands: visitBulkCommands,
        },
      },
      bulk: {
        onCommand: handleVisitBulkCommand,
      },
      displaySettings: visitCalendarLayoutSettings,
      // One view, one filter set: the Layout control in display options flips Table/Calendar
      // instead of asking people to keep a saved view per layout.
      //
      // The period control is declared here rather than per mount, so every Visits board — the
      // entity board and the Customer, Site and Service Plan tabs — bounds its read the same way.
      header: { title: 'Visits', hideAdd: true, showSearch: true, showPaginator: true, showViewOptions: true, showDisplaySwitcher: true },
      empty: { title: 'No matching visits', description: 'There are no visits matching this view and its filters.' },
    },
  },
  surfaces: {
    [SurfaceType.Manage]: () => import('./surface/manage/visit.manage.surface').then(module => module.visitManageSurface),
    [SurfaceType.Detail]: () => import('./surface/detail/visit.detail.surface').then(module => module.visitDetailSurface),
  },
  operations: {
    edit: { surface: SurfaceType.Manage, authorize: ({ injector }) => injector.get(RightsService).hasRights(JobSecurityRight.CanAccess) },
    // Safe to swap the model rather than remount between Visits: the Detail factory derives nothing
    // per-record at build time (no initialize, no record-shaped schema, no record-derived
    // initialReadonly) and its onPatch refuses — Visit writes go through lifecycle commands, so no
    // persistence closure holds the first record's id. See EntityManifestOperationSupportsModelSwap.
    preview: { surface: SurfaceType.Detail, supportsModelSwap: true, authorize: ({ injector }) => injector.get(RightsService).hasRights(JobSecurityRight.CanAccess) },
  },
  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/visit.activities.relation').then(module => module.visitActivitiesRelation),
    [ActivityEntityName.PlannedItem]: () => import('./relations/visit.items.relation').then(module => module.visitItemsBoardBuilder),
  },
  domains: {
    [DomainType.ClientPortal]: {
      // No portal Visits page: the board mounts on Site (and Service Line). Root is required by the
      // routes type; `/sites` is the page that hosts the board. `record: null` hides Open full page.
      routes: {
        root: '/sites',
        record: () => null,
      },
      presentation: visitPresentation,
      surfaces: {
        [SurfaceType.Detail]: () => import('./surface/detail/visit.detail.surface').then(module => module.visitDetailSurface),
      },
      operations: {
        preview: { surface: SurfaceType.Detail, supportsModelSwap: true, authorize: ({ injector }) => injector.get(RightsService).hasRights(PortalClientSecurityRight.CanAccessServices) },
      },
      board: {
        supportedDisplays: [EntityViewDisplay.Table, EntityViewDisplay.Calendar],
        calendar: visitCalendar,
        defaults: {
          /**
           * Same bounded read as the tenant board, repeated because a non-Tenant manifest is built
           * from this block ALONE. Table-only: Calendar bounds itself by the grid it lays out, and
           * the period pill still drives the grid so picking September redraws September.
           */
          period: {
            field: 'startDate',
            presets: [...FILTER_PERIOD_PRESETS],
            defaultPreset: () => FilterPeriodPreset.ThisMonth,
            custom: true,
            displays: [EntityViewDisplay.Table],
          },
          paging: 'hasMore',
          readonly: true,
          table: {
            fillerColumn: true,
            columns: {
              byField: {
                startDate: { headerName: 'Date / time', ...PlannedWorkCol.dateTime },
                serviceLine: PlannedWorkCol.line,
                status: { headerName: 'Status', ...PlannedWorkCol.status },
                job: PlannedWorkCol.job,
              },
            },
          },
          displaySettings: visitCalendarLayoutSettings,
          header: { title: 'Visits', hideAdd: true, showSearch: true, showPaginator: true, showViewOptions: { selector: false }, showDisplaySwitcher: true },
          empty: { title: 'No matching visits', description: 'There are no visits matching this view and its filters.' },
        },
      },
    },
  },
});
